'use client';

import {
  useAuth,
  useGetConnection,
  useRealtimeStatus,
  useSdkTurnkey,
} from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import { AppDownloadAction } from '@/components/app-upsells/app-download-action';
import { MarketsHowItWorksModal } from '@/components/how-it-works/markets-how-it-works-modal';
import { LOCALE_NAMES } from '@/components/language-switcher/locale-names';
import { SidebarListRow } from '@/components/layout/sidebar-list-row';
import { SidebarSection } from '@/components/layout/sidebar-section';
import { LocaleLocationModal, type LocaleLocationTab } from '@/components/locale-location-modal';
import {
  Award01Icon,
  BookOpen02Icon,
  BubbleChatQuestionIcon,
  ConstructionIconSolid,
  DollarCircleIcon,
  DropletIcon,
  GiftIcon,
  Globe02Icon,
  IdeaIcon,
  Location01Icon,
  Login01Icon,
  Logout01Icon,
  MarketAnalysisIcon,
  Message01Icon,
  MoneyBag01IconSolid,
  Moon02Icon,
  Pulse02Icon,
  Settings02Icon,
} from '@/components/ui/icon/glyphs';
import { getCountryByCode } from '@/config/countries';
import { getCurrencyByCode } from '@/config/currencies';
import { DOCS_URL } from '@/config/docs-links';
import { CANNY_FEATURE_BOARD_URL } from '@/config/feedback';
import { isMorfiMarketsEmail, OPERATOR_DASHBOARD_URL } from '@/config/operator-dashboard';
import { onEvent } from '@/events';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { captureReturnToAfterAuth, setNextAfterAuth } from '@/lib/auth/next-url';
import { COMPETITIONS_PATH } from '@/lib/competitions/paths';
import { isMainnetEnabled } from '@/lib/network/mainnet-enabled';
import { useSwitchNetwork } from '@/lib/network/switcher';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { setThemeWithTransition } from '@/lib/ui/theme-transition';
import { useCurrencyStore } from '@/stores/currency';
import { useGraduationModalStore } from '@/stores/graduation-modal';
import { useLayoutStore } from '@/stores/layout';
import { useMainnetConstructionModalStore } from '@/stores/mainnet-construction-modal';
import { useManipulationModalStore } from '@/stores/manipulation-modal';
import { useNetworkTransitionStore } from '@/stores/network-transition';
import { openSupport } from '@/stores/support';

export interface SidebarContentProps {
  /** Fired after rows that navigate (Settings link, Logout, Connect) or
   *  trigger external/side-effect actions (Documentation, Feedback, Support).
   *  Used by the mobile-menu sheet to close itself. Rows that open an in-app
   *  modal (Language, Location, Currency, How it works) do NOT fire this —
   *  closing the sheet would unmount the modal mid-open. Theme and Demo Mode
   *  also stay in place (toggles, not navigations). */
  onAfterAction?: () => void;
}

export function SidebarContent({ onAfterAction }: SidebarContentProps = {}): React.ReactNode {
  const t = useTranslations('sidebar');
  const tHeader = useTranslations('header.settings');
  const tMobile = useTranslations('mobile_menu');
  const tWallet = useTranslations('rewards.userMenu');
  const tNav = useTranslations('nav');
  const tFeedback = useTranslations('feedback');
  const tCompetitions = useTranslations('competitions.list');

  const locale = useLocale();
  const { theme, setTheme } = useTheme();
  const { isAuthenticated, user, logout } = useAuth();
  const currency = useCurrencyStore((s) => s.currency);
  const networkMode = useNetworkMode();
  const showMainnetSwitch = useFeatureGate('mainnet_switch') === true;
  const switchNetwork = useSwitchNetwork();
  const openMainnetConstructionModal = useMainnetConstructionModalStore((s) => s.setOpen);
  const setFaucetModalOpen = useLayoutStore((s) => s.setFaucetModalOpen);
  const openFeedback = useLayoutStore((s) => s.openFeedback);
  // Faucet (test cash) is available whenever the viewer is not on mainnet —
  // matches the portfolio FaucetBanner gating.
  const showFaucet = networkMode !== 'mainnet';
  const showRewards = useFeatureGate('rewards_platform') === true;
  const showCompetitions = useFeatureGate('trading_competitions') === true;
  const showPortfolioInSidebar = useFeatureGate('live_events') === true && isAuthenticated;
  const openGraduation = useGraduationModalStore((s) => s.setOpen);
  const openManipulation = useManipulationModalStore((s) => s.setOpen);
  const transitionInFlight = useNetworkTransitionStore((s) => s.from !== null);
  const { handleLogin } = useSdkTurnkey();

  const mounted = useHydrated();
  const [localeModalOpen, setLocaleModalOpen] = useState(false);
  const [localeModalInitialTab, setLocaleModalInitialTab] = useState<LocaleLocationTab>('language');
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  function openLocaleModal(tab: LocaleLocationTab): void {
    setLocaleModalInitialTab(tab);
    setLocaleModalOpen(true);
  }

  const currentLanguageName =
    locale in LOCALE_NAMES ? LOCALE_NAMES[locale as keyof typeof LOCALE_NAMES] : locale;
  const currentCountryName =
    user?.country !== undefined && user.country !== null && user.country.length > 0
      ? (getCountryByCode(user.country)?.name ?? user.country)
      : undefined;
  const currentCurrencyName = getCurrencyByCode(currency)?.name ?? currency;

  // Live-data (NATS realtime) connection status. `useRealtimeStatus` reports
  // `connecting` until the FIRST live socket (vs the FSM's optimistic default),
  // and collapses every non-CONNECTED state to reconnecting/offline so the dot
  // is only green when realtime is genuinely live.
  const realtimeStatus = useRealtimeStatus();
  // ALLOW-UNSCOPED: connectivity FSM hook — no data payload / fields param.
  const { forceReconnect } = useGetConnection();
  const liveDataTone: 'success' | 'warning' | 'destructive' =
    realtimeStatus === 'connected'
      ? 'success'
      : realtimeStatus === 'offline'
        ? 'destructive'
        : 'warning';
  const liveDataLabel =
    realtimeStatus === 'connected'
      ? t('live_data_connected')
      : realtimeStatus === 'offline'
        ? t('live_data_offline')
        : realtimeStatus === 'reconnecting'
          ? t('live_data_reconnecting')
          : t('live_data_connecting');

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-4">
      <SidebarSection id="settings" title={t('section_settings')}>
        <SidebarListRow
          icon={Location01Icon}
          label={tHeader('location')}
          trailingLabel={currentCountryName}
          disabled={!isAuthenticated}
          onClick={(): void => openLocaleModal('location')}
        />

        <SidebarListRow
          icon={Globe02Icon}
          label={tHeader('language')}
          trailingLabel={currentLanguageName}
          onClick={(): void => openLocaleModal('language')}
        />

        <SidebarListRow
          icon={DollarCircleIcon}
          label={tHeader('currency')}
          // Currency is read from localStorage/cookie (client-only), so SSR
          // renders the 'USD' default while the client's first paint reads the
          // persisted choice — gate on `mounted` to avoid a hydration mismatch,
          // same as the theme row below.
          trailingLabel={mounted ? currentCurrencyName : undefined}
          onClick={(): void => openLocaleModal('currency')}
        />

        <SidebarListRow
          icon={Moon02Icon}
          label={t('theme')}
          trailingLabel={
            mounted ? (theme === 'dark' ? t('theme_dark') : t('theme_light')) : undefined
          }
          trailingAction="switch"
          switchChecked={mounted && theme === 'dark'}
          onClick={(): void => {
            setThemeWithTransition(setTheme, theme === 'dark' ? 'light' : 'dark');
          }}
        />

        {showMainnetSwitch ? (
          <SidebarListRow
            icon={ConstructionIconSolid}
            label={t('demo_mode')}
            trailingLabel={networkMode === 'devnet' ? t('on') : t('off')}
            trailingAction="switch"
            switchChecked={networkMode === 'devnet'}
            switchDisabled={transitionInFlight}
            disabled={transitionInFlight}
            onClick={(): void => {
              const nextNetwork = networkMode === 'mainnet' ? 'devnet' : 'mainnet';
              if (nextNetwork === 'mainnet' && !isMainnetEnabled()) {
                openMainnetConstructionModal(true);
                return;
              }
              switchNetwork(nextNetwork);
            }}
          />
        ) : null}

        <SidebarListRow
          icon={Pulse02Icon}
          label={t('live_data')}
          trailingLabel={liveDataLabel}
          trailingAction="none"
          statusTone={liveDataTone}
          onClick={(): void => forceReconnect()}
        />
      </SidebarSection>

      <SidebarSection id="account" title={t('section_account')} className="mt-auto">
        {showPortfolioInSidebar ? (
          <SidebarListRow
            icon={MoneyBag01IconSolid}
            label={tNav('portfolio')}
            href={user?.id ? `/portfolio/${user.id}` : '/portfolio'}
            onAfterClick={onAfterAction}
          />
        ) : null}
        {showFaucet ? (
          <SidebarListRow
            icon={DropletIcon}
            label={tNav('faucet')}
            onClick={(): void => setFaucetModalOpen(true)}
            onAfterClick={onAfterAction}
          />
        ) : null}
        {showRewards ? (
          <SidebarListRow
            icon={GiftIcon}
            label={t('rewards')}
            href={isAuthenticated ? '/rewards' : undefined}
            onClick={(): void => {
              onEvent('rewards_entry_clicked', {
                authenticated: isAuthenticated,
                source: 'sidebar',
              });
              if (isAuthenticated) return;
              setNextAfterAuth('/rewards');
              handleLogin();
            }}
            onAfterClick={onAfterAction}
          />
        ) : null}
        <SidebarListRow
          icon={Settings02Icon}
          iconSize="sm"
          label={tHeader('title')}
          href="/settings"
          onAfterClick={onAfterAction}
        />
        {isAuthenticated ? (
          <SidebarListRow
            icon={Logout01Icon}
            iconSize="sm"
            label={tMobile('log_out')}
            destructive
            onClick={(): void => {
              void logout();
            }}
            onAfterClick={onAfterAction}
          />
        ) : (
          <SidebarListRow
            icon={Login01Icon}
            iconSize="sm"
            label={tMobile('connect')}
            onClick={(): void => {
              captureReturnToAfterAuth();
              handleLogin();
            }}
            onAfterClick={onAfterAction}
          />
        )}
      </SidebarSection>

      <SidebarSection id="resources" title={t('section_resources')}>
        {showCompetitions ? (
          <SidebarListRow
            icon={Award01Icon}
            label={tCompetitions('title')}
            href={COMPETITIONS_PATH}
            onAfterClick={onAfterAction}
          />
        ) : null}
        {isMorfiMarketsEmail(user?.email) ? (
          <SidebarListRow
            icon={Settings02Icon}
            label={tWallet('dashboard')}
            href={OPERATOR_DASHBOARD_URL}
            onAfterClick={onAfterAction}
          />
        ) : null}
        <SidebarListRow
          icon={MarketAnalysisIcon}
          label={tMobile('how_it_works')}
          onClick={(): void => setHowItWorksOpen(true)}
        />
        <SidebarListRow
          icon={BookOpen02Icon}
          label={tMobile('documentation')}
          href={DOCS_URL}
          onAfterClick={onAfterAction}
        />
        <SidebarListRow
          icon={BubbleChatQuestionIcon}
          label={tMobile('support')}
          onClick={(): void => {
            openSupport();
          }}
          onAfterClick={onAfterAction}
        />
        <SidebarListRow
          icon={Message01Icon}
          label={tFeedback('menu.send_feedback')}
          onClick={(): void => {
            onEvent('feedback_opened', { source: 'sidebar', intent: 'feedback' });
            openFeedback();
          }}
          onAfterClick={onAfterAction}
        />
        <SidebarListRow
          icon={IdeaIcon}
          label={tFeedback('menu.feature_board')}
          href={CANNY_FEATURE_BOARD_URL}
          onAfterClick={onAfterAction}
        />
        <AppDownloadAction surface="navigation" onAfterAction={onAfterAction} />
      </SidebarSection>

      <LocaleLocationModal
        open={localeModalOpen}
        onOpenChange={setLocaleModalOpen}
        initialTab={localeModalInitialTab}
      />
      <MarketsHowItWorksModal
        open={howItWorksOpen}
        onOpenChange={setHowItWorksOpen}
        onWhatIsGraduation={(): void => {
          setHowItWorksOpen(false);
          openGraduation(true);
        }}
        onPreventManipulation={(): void => {
          setHowItWorksOpen(false);
          openManipulation(true);
        }}
      />
    </div>
  );
}
